/**
 * The small statuses beside the Prospects title: work in progress, how far the list can be trusted,
 * and whether the scoring model has been approved.
 *
 * Each used to be a full-width alert. A sync or a scoring pass is now a spinner with a few words. The
 * reasons the list is less reliable open from a "Data quality" chip, with a way to fix the ones a
 * mapping fixes. An unapproved model is a "Preview" badge that says what it means when pointed at.
 */
import { CircleAlert } from 'lucide-react';
import {
  Badge,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@design-system';
import { FIXED_BY_MAPPING, type BookNotices } from '../lib/book-notices';
import { CONFIDENCE_LABELS } from '../lib/labels';

interface BookStatusChipsProps {
  crmName: string;
  notices: BookNotices;
  onOpenFieldMapping: () => void;
}

export function BookStatusChips({ crmName, notices, onOpenFieldMapping }: BookStatusChipsProps) {
  const fixable = notices.qualityReasons.some(reason => FIXED_BY_MAPPING.has(reason));

  return (
    <>
      {notices.isPreview && (
        <Tooltip>
          {/* Badge takes no ref, and without one the tooltip has nothing to anchor to. */}
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              className="focus-visible:ring-primary inline-flex rounded-full focus-visible:ring-2 focus-visible:outline-none"
            >
              <Badge variant="warning">Preview</Badge>
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            This list is scored with a preview model that has not been approved for use yet.
          </TooltipContent>
        </Tooltip>
      )}

      {notices.activity && (
        <span
          role="status"
          className="text-primary inline-flex items-center gap-1.5 text-sm font-medium"
        >
          <Spinner aria-hidden="true" />
          {notices.activity === 'syncing' ? `Syncing your ${crmName} contacts…` : 'Scoring your contacts…'}
        </span>
      )}

      {notices.qualityReasons.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 border-yellow-600/30 text-yellow-800"
            >
              <CircleAlert className="h-3.5 w-3.5" />
              Data quality: Limited
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-96 max-w-[calc(100vw-2rem)] text-sm"
          >
            <p className="font-semibold text-gray-900">Some priorities may be less reliable</p>
            <ul className="mt-2 list-disc space-y-1 pl-4 text-gray-700">
              {notices.qualityReasons.map(reason => (
                <li key={reason}>{CONFIDENCE_LABELS[reason]}</li>
              ))}
            </ul>
            {fixable && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={onOpenFieldMapping}
              >
                Open field mapping
              </Button>
            )}
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}
